import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { DUPE_API_URL } from '@dupe/common/constants'
import type {
  CanonicalReportProduct,
  CanonicalReportRetailerLink,
  CanonicalReportSourceSummary,
  SeoProjection,
} from '@dupe/common/research-report'
import type { ResearchBuyLink } from '@dupe/common/types'
import fetcher from '~/src/lib/fetcher'
import {
  SEO_REPORT_CLICK_SOURCE,
  withRedirectClickSource,
} from '~/src/lib/withRedirectClickSource'

type RetailerLinksResponse = {
  ok: boolean
  retailerLinks?: {
    results?: ResearchBuyLink[]
    priceSourceResults?: ResearchBuyLink[]
  }
}

export interface SeoRenderableBuyLink {
  label: string
  url: string
  price?: number | null
  logoUrl?: string | null
  verified?: boolean
}

type SeoReportRetailerLinksSnapshot = {
  sourceTaskId?: string | null
  document?: {
    products?: CanonicalReportProduct[] | null
  } | null
  seo?: Pick<SeoProjection, 'rankedProducts' | 'topPicks'> | null
}

interface SeoReportRetailerLinksOptions {
  enabled?: boolean
}

const SEO_REPORT_RETAILER_LINKS_STALE_TIME_MS = 5 * 60 * 1000
const SEO_REPORT_RETAILER_LINKS_GC_TIME_MS = 30 * 60 * 1000

function normalizeFaviconOrigin(
  value: string | null | undefined
): string | null {
  const trimmed = value?.trim()
  if (!trimmed) return null

  try {
    const parsed = new URL(
      trimmed.includes('://') ? trimmed : `https://${trimmed}`
    )
    return parsed.origin
  } catch {
    return null
  }
}

function resolveRetailerOrigin(url: string): string | null {
  try {
    const parsed = new URL(url, 'https://dupe.com')
    const isDupeRedirect =
      parsed.pathname.startsWith('/redirect') ||
      parsed.hostname === 'dupe.com' ||
      parsed.hostname === 'dupe.link'

    if (isDupeRedirect) {
      const shopKey = normalizeFaviconOrigin(parsed.searchParams.get('shopKey'))
      if (shopKey) return shopKey

      const link = normalizeFaviconOrigin(parsed.searchParams.get('link'))
      if (link) return link
    }

    return parsed.origin
  } catch {
    return null
  }
}

function getFaviconUrl(urlOrRetailerKey: string): string | null {
  const trimmed = urlOrRetailerKey.trim()
  const looksLikeUrl =
    trimmed.startsWith('/') || /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
  const origin = looksLikeUrl
    ? resolveRetailerOrigin(trimmed)
    : normalizeFaviconOrigin(trimmed)
  if (!origin) return null

  return `https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=${encodeURIComponent(origin)}&size=96`
}

function normalizePrice(
  value: number | string | null | undefined
): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value
  }

  if (typeof value !== 'string') return null

  const match = value.replace(/,/g, '').match(/\d+(?:\.\d+)?/)
  if (!match) return null

  const parsed = Number(match[0])
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function getRetailerLinkKey(label: string, url: string): string {
  const normalizedLabel = label.toLowerCase().replace(/[^a-z0-9]+/g, '')
  const origin = resolveRetailerOrigin(url)
  const normalizedOrigin = origin
    ? origin
        .replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .toLowerCase()
    : url.trim().toLowerCase()

  return `${normalizedLabel}:${normalizedOrigin}`
}

function getProductFallbackPrice(
  product: CanonicalReportProduct | null | undefined
): number | null {
  if (!product) return null

  return (
    normalizePrice(product.pricing.observedLow) ??
    normalizePrice(product.pricing.msrp) ??
    normalizePrice(product.pricing.observedHigh)
  )
}

function getDomainLabel(url: string): string | null {
  try {
    return (
      new URL(url).hostname
        .replace(/^www\./i, '')
        .split('.')
        .filter(Boolean)[0]
        ?.replace(/\b\w/g, (character) => character.toUpperCase()) ?? null
    )
  } catch {
    return null
  }
}

function normalizeSourceText(value: string | null | undefined): string {
  return (value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function getUrlParts(
  url: string
): { hostname: string; pathname: string } | null {
  try {
    const parsed = new URL(url)
    return {
      hostname: parsed.hostname.replace(/^www\./i, '').toLowerCase(),
      pathname: parsed.pathname.replace(/\/+$/g, ''),
    }
  } catch {
    return null
  }
}

function isOfficialOrBrandProductSource(
  source: CanonicalReportSourceSummary,
  product: CanonicalReportProduct
): boolean {
  const label = source.label?.toLowerCase() ?? ''
  if (label.includes('official product page')) return true
  if (label.includes('retailer product page')) return true
  if (label.includes('product page')) return true

  const brand = normalizeSourceText(product.brand)
  if (!brand) return false

  const normalizedLabel = normalizeSourceText(source.label)
  if (normalizedLabel === brand) return true
  if (brand.length < 4) return false

  const parts = getUrlParts(source.url)
  return parts ? normalizeSourceText(parts.hostname).includes(brand) : false
}

function isDirectProductSource(source: CanonicalReportSourceSummary): boolean {
  const parts = getUrlParts(source.url)
  if (!parts) return false

  const pathSegments = parts.pathname.split('/').filter(Boolean)
  if (pathSegments.length === 0) return false

  return !parts.pathname.match(
    /\/(blog|blogs|guide|guides|review|reviews|news|article|articles)\b/i
  )
}

// A source counts as "brand-owned" only when its label says so explicitly OR
// its hostname includes the brand name (e.g. brooksrunning.com for Brooks).
// This separates the two kinds of fallback links emitted by
// `buildOfficialSourceLinks` so we can label them correctly:
//   - brand-owned   → use product.brand ("Nike", "Brooks")
//   - retailer      → use the retailer's domain/label, NOT the brand name
function isBrandOwnedSource(
  source: CanonicalReportSourceSummary,
  product: CanonicalReportProduct
): boolean {
  const label = source.label?.toLowerCase() ?? ''
  if (label.includes('retailer')) return false
  if (label.includes('official')) return true

  const brand = normalizeSourceText(product.brand)
  if (!brand || brand.length < 4) return false
  const parts = getUrlParts(source.url)
  return parts ? normalizeSourceText(parts.hostname).includes(brand) : false
}

function mapOfficialSourceLink(
  source: CanonicalReportSourceSummary,
  product: CanonicalReportProduct
): SeoRenderableBuyLink {
  // Retailer fallback links (Amazon, Target, etc.) shouldn't be labeled
  // with the product's brand — that reads as "Nike" on a Target listing.
  // Use the retailer's domain-derived label (or its own label) instead, and
  // reserve the brand label for actually-official brand-owned URLs.
  const brandOwned = isBrandOwnedSource(source, product)
  const label = brandOwned
    ? (product.brand ??
      source.label ??
      getDomainLabel(source.url) ??
      product.name)
    : (getDomainLabel(source.url) ??
      source.label ??
      product.brand ??
      product.name)
  return {
    label,
    url: source.url,
    price: null,
    logoUrl: getFaviconUrl(source.url),
    verified: undefined,
  }
}

function buildOfficialSourceLinks(
  product: CanonicalReportProduct
): SeoRenderableBuyLink[] {
  return product.sources
    .filter((source) => isOfficialOrBrandProductSource(source, product))
    .sort(
      (a, b) =>
        Number(isDirectProductSource(b)) - Number(isDirectProductSource(a))
    )
    .map((source) => mapOfficialSourceLink(source, product))
    .slice(0, 3)
}

function mapCanonicalLink(
  link: CanonicalReportRetailerLink
): SeoRenderableBuyLink {
  return {
    label: link.label,
    url: link.url,
    price: normalizePrice(link.price),
    logoUrl: getFaviconUrl(link.url),
    verified: undefined,
  }
}

function mapResearchBuyLink(link: ResearchBuyLink): SeoRenderableBuyLink {
  return {
    label: link.retailerName,
    url: link.redirectUrl,
    price: normalizePrice(link.extractedPrice) ?? normalizePrice(link.price),
    logoUrl: link.favicon ?? getFaviconUrl(link.retailerKey),
    verified: link.isVerified,
  }
}

function mergeBuyLink(
  existing: SeoRenderableBuyLink,
  incoming: SeoRenderableBuyLink
): SeoRenderableBuyLink {
  return {
    ...existing,
    price: existing.price ?? incoming.price ?? null,
    logoUrl: existing.logoUrl ?? incoming.logoUrl ?? null,
    verified: existing.verified || incoming.verified,
  }
}

function dedupeLinks(links: SeoRenderableBuyLink[]): SeoRenderableBuyLink[] {
  const indexByKey = new Map<string, number>()
  const deduped: SeoRenderableBuyLink[] = []

  for (const link of links) {
    const key = getRetailerLinkKey(link.label, link.url)
    const existingIndex = indexByKey.get(key)
    if (existingIndex != null) {
      const existing = deduped[existingIndex]
      if (existing) {
        deduped[existingIndex] = mergeBuyLink(existing, link)
      }
      continue
    }

    indexByKey.set(key, deduped.length)
    deduped.push(link)
  }

  return deduped
}

function parseRetailerLinksResponse(
  data: RetailerLinksResponse | undefined
): SeoRenderableBuyLink[] {
  const results = (data?.retailerLinks?.results ?? []).map(mapResearchBuyLink)
  const priceSources = (data?.retailerLinks?.priceSourceResults ?? []).map(
    mapResearchBuyLink
  )

  if (results.length > 0) {
    return dedupeLinks([...results, ...priceSources]).slice(0, 3)
  }

  return dedupeLinks(priceSources).slice(0, 3)
}

function buildCanonicalBuyLinks(
  links: CanonicalReportRetailerLink[] | undefined,
  product: CanonicalReportProduct | null | undefined
): SeoRenderableBuyLink[] {
  const priceSourceKeys = new Set(
    (product?.retailerLinks?.priceSourceLinks ?? []).map((link) =>
      getRetailerLinkKey(link.label, link.url)
    )
  )
  const fallbackPrice = getProductFallbackPrice(product)

  return (links ?? []).map((link) => {
    const mapped = mapCanonicalLink(link)
    const hasPriceSource = priceSourceKeys.has(
      getRetailerLinkKey(link.label, link.url)
    )

    if (mapped.price == null && hasPriceSource && fallbackPrice != null) {
      return { ...mapped, price: fallbackPrice }
    }

    return mapped
  })
}

function buildSnapshotBuyLinks(
  links: CanonicalReportRetailerLink[] | undefined,
  product: CanonicalReportProduct | null | undefined
): SeoRenderableBuyLink[] {
  const canonicalLinks = buildCanonicalBuyLinks(links, product)
  if (canonicalLinks.length > 0 || !product) return canonicalLinks

  return buildOfficialSourceLinks(product)
}

function buildSnapshotLinksBySlug(
  report: SeoReportRetailerLinksSnapshot,
  slugs: string[]
): Map<string, SeoRenderableBuyLink[]> {
  const productsBySlug = new Map<string, CanonicalReportProduct>()
  for (const product of report.document?.products ?? []) {
    productsBySlug.set(product.slug, product)
  }
  for (const product of report.seo?.rankedProducts ?? []) {
    productsBySlug.set(product.slug, product)
  }

  const topPickLinks = new Map(
    (report.seo?.topPicks ?? []).map((pick) => {
      const product = productsBySlug.get(pick.slug)
      return [
        pick.slug,
        buildSnapshotBuyLinks(
          pick.buyLinks ?? product?.retailerLinks?.buyLinks,
          product
        ),
      ] as const
    })
  )
  const rankedLinks = new Map(
    (report.seo?.rankedProducts ?? []).map((product) => {
      return [
        product.slug,
        buildSnapshotBuyLinks(product.retailerLinks?.buyLinks, product),
      ] as const
    })
  )

  const bySlug = new Map<string, SeoRenderableBuyLink[]>()
  for (const slug of slugs) {
    const topPick = topPickLinks.get(slug)
    const ranked = rankedLinks.get(slug)
    bySlug.set(slug, topPick && topPick.length > 0 ? topPick : (ranked ?? []))
  }

  return bySlug
}

export function useSeoReportRetailerLinks(
  report: SeoReportRetailerLinksSnapshot,
  slugs: string[],
  options: SeoReportRetailerLinksOptions = {}
): Map<string, SeoRenderableBuyLink[]> {
  const sourceTaskId = report.sourceTaskId ?? undefined
  const fetchLiveLinks = options.enabled ?? true

  const uniqueSlugs = useMemo(() => {
    return Array.from(new Set(slugs.filter(Boolean)))
  }, [slugs])

  const snapshotLinksBySlug = useMemo(() => {
    return buildSnapshotLinksBySlug(report, uniqueSlugs)
  }, [report, uniqueSlugs])

  const queryResults = useQueries({
    queries: uniqueSlugs.map((slug) => ({
      queryKey: ['seo-report-retailer-links', sourceTaskId, slug],
      // `options.enabled` (e.g. drawer-open) gates the live fetch to keep
      // first paint and crawlers off the JSON endpoint — EXCEPT for slugs
      // whose snapshot has zero links. Those fetch immediately: an empty
      // buy-link list is worse than one extra request, and the live
      // endpoint can usually recover links the baked snapshot is missing.
      enabled:
        (fetchLiveLinks ||
          (snapshotLinksBySlug.get(slug)?.length ?? 0) === 0) &&
        typeof window !== 'undefined' &&
        Boolean(sourceTaskId),
      staleTime: SEO_REPORT_RETAILER_LINKS_STALE_TIME_MS,
      gcTime: SEO_REPORT_RETAILER_LINKS_GC_TIME_MS,
      refetchOnWindowFocus: true,
      retry: 1,
      queryFn: async () => {
        return fetcher<RetailerLinksResponse>(
          `${DUPE_API_URL}/api/research/${sourceTaskId}/product/${slug}/retailer-links`,
          { method: 'GET', axiosConfig: { withCredentials: false } }
        )
      },
      select: parseRetailerLinksResponse,
    })),
  })

  return useMemo(() => {
    const merged = new Map(snapshotLinksBySlug)

    queryResults.forEach((queryResult, index) => {
      const slug = uniqueSlugs[index]
      if (!slug) return
      if (queryResult.status === 'success' && queryResult.data.length > 0) {
        merged.set(slug, queryResult.data)
      }
    })

    for (const [slug, links] of merged) {
      merged.set(
        slug,
        links.map((link) => ({
          ...link,
          url: withRedirectClickSource(link.url, SEO_REPORT_CLICK_SOURCE),
        }))
      )
    }

    return merged
  }, [queryResults, snapshotLinksBySlug, uniqueSlugs])
}
